import {
  type Catalogue,
  type CatalogueSet,
  DEFAULT_LOCALE,
  type Locale,
  type UseCase,
} from './types';

/**
 * Catalogue file names to consult for a locale and use case, most specific
 * first. `en-US` + `survey` gives:
 * `en-US.survey, en-US, en.survey, en, en-GB.survey, en-GB`.
 */
export function catalogueLayers(locale: Locale, useCase: UseCase): string[] {
  const tags = new Set<string>();
  const parts = locale.split('-');
  for (let i = parts.length; i >= 1; i -= 1)
    tags.add(parts.slice(0, i).join('-'));
  const baseParts = DEFAULT_LOCALE.split('-');
  for (let i = baseParts.length; i >= 1; i -= 1)
    tags.add(baseParts.slice(0, i).join('-'));
  const layers: string[] = [];
  for (const tag of tags) layers.push(`${tag}.${useCase}`, tag);
  return layers;
}

/** The catalogues in `set` that apply, most specific first. */
export function stackCatalogues(
  set: CatalogueSet,
  locale: Locale,
  useCase: UseCase,
): Catalogue[] {
  const stack: Catalogue[] = [];
  for (const name of catalogueLayers(locale, useCase)) {
    const catalogue = set[name];
    if (catalogue) stack.push(catalogue);
  }
  return stack;
}

/** One flat lookup table from a stack: the first catalogue holding a key wins. */
export function flattenStack(stack: readonly Catalogue[]): Map<string, string> {
  const flat = new Map<string, string>();
  for (const catalogue of stack.toReversed()) {
    for (const [key, message] of Object.entries(catalogue))
      flat.set(key, message);
  }
  return flat;
}

export function mergeCatalogueSets(
  ...sets: readonly CatalogueSet[]
): CatalogueSet {
  const merged: Record<string, Record<string, string>> = {};
  for (const set of sets) {
    for (const [name, catalogue] of Object.entries(set)) {
      merged[name] = { ...merged[name], ...catalogue };
    }
  }
  return merged;
}

/** Locales a set can serve: file names without a use-case suffix. */
export function availableLocales(set: CatalogueSet): Locale[] {
  return Object.keys(set).filter((name) => !name.includes('.'));
}

export interface CatalogueName {
  readonly locale: Locale;
  readonly useCase: string | undefined;
}

/** Splits `en-GB.clinical` into its locale and use-case suffix. */
export function splitCatalogueName(name: string): CatalogueName {
  const dot = name.indexOf('.');
  if (dot === -1) return { locale: name, useCase: undefined };
  return { locale: name.slice(0, dot), useCase: name.slice(dot + 1) };
}
